/* =========================================================================
   PDP Product Bundle
   Slot-based "any N for X" bundle that lives inside the product form.
   Renders a picker from a source collection, resolves variants per slot,
   prices the bundle, and adds every line in a single cart request.
   ========================================================================= */

(() => {
  const MONEY_FALLBACK = "${{amount}}";

  function formatMoney(cents) {
    const value = Math.max(0, Math.round(Number(cents) || 0));

    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      try {
        return window.Shopify.formatMoney(value);
      } catch (e) {
        /* fall through to the local formatter */
      }
    }

    const format =
      (window.Shopify && window.Shopify.moneyFormat) || MONEY_FALLBACK;

    const withDelimiters = (num, precision, thousands, decimal) => {
      const fixed = (num / 100.0).toFixed(precision);
      const parts = fixed.split(".");
      const head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? head + decimal + parts[1] : head;
    };

    return format.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      (_match, token) => {
        switch (token) {
          case "amount":
            return withDelimiters(value, 2, ",", ".");
          case "amount_no_decimals":
            return withDelimiters(value, 0, ",", ".");
          case "amount_with_comma_separator":
            return withDelimiters(value, 2, ".", ",");
          case "amount_no_decimals_with_comma_separator":
            return withDelimiters(value, 0, ".", ",");
          case "amount_with_apostrophe_separator":
            return withDelimiters(value, 2, "'", ".");
          case "amount_with_space_separator":
            return withDelimiters(value, 2, " ", ",");
          case "amount_no_decimals_with_space_separator":
            return withDelimiters(value, 0, " ", ",");
          default:
            return withDelimiters(value, 2, ",", ".");
        }
      }
    );
  }

  function parseJSONScript(root, selector) {
    const node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.warn("[product-bundle] could not parse", selector, e);
      return null;
    }
  }

  class ProductBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      const catalog = parseJSONScript(this, "[data-bundle-catalog]");
      this.products = (catalog && catalog.products) || [];
      this.current = parseJSONScript(this, "[data-bundle-current]");

      this.slotCount = parseInt(this.dataset.slotCount, 10) || 2;
      this.minQty = parseInt(this.dataset.minQty, 10) || 2;
      this.lockFirst = this.dataset.lockFirst === "true";
      this.discountCode = (this.dataset.discountCode || "").trim();

      this.strings = {
        addMore: this.dataset.textAddMore || "",
        maxTier: this.dataset.textMaxTier || "",
      };

      // Tier ladder, ascending by quantity.
      this.tiers = (parseJSONScript(this, "[data-bundle-tiers]") || [])
        .filter((t) => t && t.qty > 0)
        .sort((a, b) => a.qty - b.qty);
      this.bundleName = this.dataset.bundleName || "Bundle";
      this.swatchOption = (this.dataset.swatchOption || "color").toLowerCase();
      this.redirect = this.dataset.redirect === "true";

      this.slotEls = Array.from(this.querySelectorAll("[data-bundle-slot]"));
      this.submitBtn = this.querySelector("[data-bundle-submit]");
      this.progressEl = this.querySelector("[data-bundle-progress]");

      // One state object per slot.
      this.slots = this.slotEls.map((el) => ({
        el,
        locked: el.dataset.bundleLocked === "true",
        product: null,
        options: [],
        variant: null,
      }));

      if (this.lockFirst && this.slots[0] && this.current) {
        this.slots[0].product = this.current;
        this.slots[0].options = this.defaultOptionsFor(this.current);
        this.renderSlotOptions(0);
        this.resolveVariant(0);
      }

      this.buildPickers();
      this.bindEvents();
      this.syncWithProductForm();
      this.update();
    }

    /* ---------------- variant helpers ---------------- */

    firstAvailableVariant(product) {
      return (
        product.variants.find((v) => v.available) || product.variants[0] || null
      );
    }

    defaultOptionsFor(product) {
      const variant = this.firstAvailableVariant(product);
      return variant ? variant.options.slice() : [];
    }

    // A variant is reachable if some available variant matches the chosen
    // values at every position we have already decided.
    isValueAvailable(product, optionIndex, value, chosen) {
      return product.variants.some((v) => {
        if (!v.available) return false;
        if (v.options[optionIndex] !== value) return false;
        return chosen.every(
          (val, i) => i === optionIndex || val == null || v.options[i] === val
        );
      });
    }

    resolveVariant(index) {
      const slot = this.slots[index];
      if (!slot.product) {
        slot.variant = null;
        return;
      }

      const match = slot.product.variants.find((v) =>
        v.options.every((opt, i) => opt === slot.options[i])
      );

      // Fall back to the nearest available variant so a slot is never dead.
      slot.variant = match || this.firstAvailableVariant(slot.product);
      if (slot.variant && !match) {
        slot.options = slot.variant.options.slice();
      }
    }

    /* ---------------- rendering ---------------- */

    buildPickers() {
      this.slots.forEach((slot, index) => {
        const grid = slot.el.querySelector("[data-bundle-picker-grid]");
        if (!grid) return;

        grid.innerHTML = this.products
          .map((p) => {
            const variant = this.firstAvailableVariant(p);
            const price = variant ? formatMoney(variant.price) : "";
            const img = p.featured_image
              ? `<img src="${p.featured_image}" alt="" width="150" height="200" loading="lazy">`
              : "";
            return `
              <button type="button" class="product-bundle__pick"
                data-bundle-pick="${p.id}" role="listitem" aria-pressed="false"
                ${p.available ? "" : "disabled"}>
                <span class="product-bundle__pick-media">${img}</span>
                <span class="product-bundle__pick-title">${p.title}</span>
                <span class="product-bundle__pick-price">${price}</span>
              </button>`;
          })
          .join("");

        this.refreshPickerState(index);
      });
    }

    // Grey out products already used in another slot.
    refreshPickerState(index) {
      const slot = this.slots[index];
      const grid = slot.el.querySelector("[data-bundle-picker-grid]");
      if (!grid) return;

      const takenElsewhere = this.slots
        .filter((s, i) => i !== index && s.product)
        .map((s) => String(s.product.id));

      grid.querySelectorAll("[data-bundle-pick]").forEach((btn) => {
        const id = btn.dataset.bundlePick;
        btn.classList.toggle("is-taken", takenElsewhere.includes(id));
        btn.setAttribute(
          "aria-pressed",
          slot.product && String(slot.product.id) === id ? "true" : "false"
        );
      });
    }

    renderSlotOptions(index) {
      const slot = this.slots[index];
      const host = slot.el.querySelector("[data-bundle-slot-options]");
      if (!host) return;

      if (!slot.product) {
        host.innerHTML = "";
        return;
      }

      const names = slot.product.option_names || [];
      const hasRealOptions = !(
        names.length === 1 &&
        slot.product.variants.length === 1 &&
        slot.product.variants[0].title === "Default Title"
      );

      if (!hasRealOptions) {
        host.innerHTML = "";
        return;
      }

      host.innerHTML = names
        .map((name, optionIndex) => {
          const values = [
            ...new Set(slot.product.variants.map((v) => v.options[optionIndex])),
          ];
          const isSwatch = name.toLowerCase().trim() === this.swatchOption;

          const chips = values
            .map((value) => {
              const available = this.isValueAvailable(
                slot.product,
                optionIndex,
                value,
                slot.options
              );
              const selected = slot.options[optionIndex] === value;
              const swatchColor =
                (slot.product.swatches && slot.product.swatches[value]) || "";
              const style =
                isSwatch && swatchColor
                  ? ` style="background-color:${swatchColor}"`
                  : "";
              return `
                <button type="button"
                  class="product-bundle__chip${isSwatch && swatchColor ? " product-bundle__chip--swatch" : ""}"
                  data-bundle-option="${optionIndex}"
                  data-bundle-value="${String(value).replace(/"/g, "&quot;")}"
                  role="radio"
                  aria-checked="${selected ? "true" : "false"}"
                  title="${String(value).replace(/"/g, "&quot;")}"
                  ${available ? "" : "disabled"}${style}>${value}</button>`;
            })
            .join("");

          return `
            <div class="product-bundle__opt">
              <span class="product-bundle__opt-name">${name}</span>
              <div class="product-bundle__chips" role="radiogroup" aria-label="${name}">${chips}</div>
            </div>`;
        })
        .join("");
    }

    renderSlot(index) {
      const slot = this.slots[index];
      const el = slot.el;

      const media = el.querySelector("[data-bundle-slot-media]");
      const titleEl = el.querySelector("[data-bundle-slot-title]");
      const placeholder = el.querySelector("[data-bundle-slot-placeholder]");
      const priceEl = el.querySelector("[data-bundle-slot-price]");
      const indexEl = el.querySelector("[data-bundle-slot-index]");
      const actionText = el.querySelector("[data-bundle-action-text]");
      const toggleBtn = el.querySelector("[data-bundle-toggle-picker]");
      const clearBtn = el.querySelector("[data-bundle-clear-slot]");

      el.classList.toggle("is-filled", !!slot.product);
      el.classList.toggle("is-empty", !slot.product);

      if (!slot.product) {
        if (media) media.innerHTML = "";
        if (titleEl) {
          titleEl.hidden = true;
          titleEl.textContent = "";
        }
        if (placeholder) placeholder.hidden = false;
        if (priceEl) priceEl.innerHTML = "";
        if (indexEl) indexEl.textContent = String(index + 1).padStart(2, "0");
        if (actionText && toggleBtn) {
          actionText.textContent = toggleBtn.dataset.textChoose || "Choose";
        }
        if (clearBtn) clearBtn.hidden = true;
        return;
      }

      if (placeholder) placeholder.hidden = true;
      if (clearBtn) clearBtn.hidden = false;
      if (titleEl) {
        titleEl.hidden = false;
        titleEl.textContent = slot.product.title;
      }
      if (actionText && toggleBtn) {
        actionText.textContent = toggleBtn.dataset.textChange || "Change";
      }
      if (indexEl && !indexEl.querySelector("svg")) {
        indexEl.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      }

      const variant = slot.variant;
      const image =
        (variant && variant.image) || slot.product.featured_image || "";

      if (media) {
        const existing = media.querySelector("img");
        if (existing && existing.getAttribute("src") === image) {
          existing.classList.add("is-loaded");
        } else {
          media.innerHTML = image
            ? `<img src="${image}" alt="${slot.product.title.replace(/"/g, "&quot;")}" width="66" height="88" loading="lazy">`
            : "";
          const img = media.querySelector("img");
          if (img) {
            if (img.complete) img.classList.add("is-loaded");
            else img.addEventListener("load", () => img.classList.add("is-loaded"), { once: true });
          }
        }
      }

      if (priceEl && variant) {
        const compare = variant.compare_at_price || 0;
        priceEl.innerHTML =
          compare > variant.price
            ? `<s>${formatMoney(compare)}</s><span>${formatMoney(variant.price)}</span>`
            : `<span>${formatMoney(variant.price)}</span>`;
      }
    }

    /* ---------------- pricing ---------------- */

    // Deepest tier the current item count has earned.
    tierFor(count) {
      let found = null;
      this.tiers.forEach((t) => {
        if (count >= t.qty) found = t;
      });
      return found;
    }

    // The next tier still worth reaching, if any.
    nextTier(count) {
      return this.tiers.find((t) => t.qty > count) || null;
    }

    prices() {
      const filled = this.slots.filter((s) => s.variant);
      const count = filled.length;
      const subtotal = filled.reduce((sum, s) => sum + s.variant.price, 0);
      const compareSum = filled.reduce(
        (sum, s) => sum + (s.variant.compare_at_price || s.variant.price),
        0
      );

      const tier = this.tierFor(count);
      const next = this.nextTier(count);
      const discount = tier ? tier.discount : 0;

      const total = discount
        ? Math.round(subtotal * (1 - discount / 100))
        : subtotal;

      const reference = Math.max(subtotal, compareSum);

      return {
        // A bundle is addable once it reaches the smallest tier.
        complete: count >= this.minQty,
        filledCount: count,
        subtotal,
        total,
        reference,
        savings: Math.max(0, reference - total),
        tier,
        next,
        discount,
      };
    }

    update() {
      this.slots.forEach((_, i) => {
        this.renderSlot(i);
        this.refreshPickerState(i);
      });

      const p = this.prices();

      const totalEl = this.querySelector("[data-bundle-total]");
      const compareEl = this.querySelector("[data-bundle-compare]");
      const savingsEl = this.querySelector("[data-bundle-savings]");

      if (totalEl) totalEl.textContent = formatMoney(p.total);

      if (compareEl) {
        const show = p.savings > 0;
        compareEl.hidden = !show;
        compareEl.textContent = show ? formatMoney(p.reference) : "";
      }

      if (savingsEl) {
        const show = p.savings > 0;
        savingsEl.hidden = !show;
        savingsEl.textContent = show
          ? `${savingsEl.dataset.label || "You save"} ${formatMoney(p.savings)}`
          : "";
      }

      if (this.progressEl) {
        this.progressEl.style.width = `${(p.filledCount / this.slots.length) * 100}%`;
      }

      // Tier ladder: mark everything earned, and highlight the active rung.
      this.querySelectorAll("[data-tier-qty]").forEach((el) => {
        const qty = parseInt(el.dataset.tierQty, 10);
        el.classList.toggle("is-reached", p.filledCount >= qty);
        el.classList.toggle(
          "is-active",
          !!p.tier && p.tier.qty === qty
        );
      });

      // Hint: how far to the next tier, or confirmation of the one earned.
      const hintEl = this.querySelector("[data-bundle-hint]");
      if (hintEl) {
        let hint = "";
        if (p.next) {
          const need = p.next.qty - p.filledCount;
          hint = (this.strings.addMore || "")
            .replace("{{ count }}", need)
            .replace("{{ percent }}", p.next.discount);
        } else if (p.tier) {
          hint = (this.strings.maxTier || "").replace(
            "{{ percent }}",
            p.tier.discount
          );
        }
        hintEl.textContent = hint;
        hintEl.hidden = !hint;
      }

      if (this.submitBtn) {
        this.submitBtn.disabled = !p.complete;
        let label;
        if (!p.complete) {
          label =
            this.submitBtn.dataset.textIncomplete ||
            this.submitBtn.dataset.textDefault;
        } else {
          label = this.submitBtn.dataset.textDefault;
          if (p.discount) {
            label = `${label} · ${p.discount}%`;
          }
        }
        if (!this.submitBtn.classList.contains("is-loading")) {
          this.submitBtn.textContent = label;
        }
      }
    }

    /* ---------------- events ---------------- */

    bindEvents() {
      this.addEventListener("click", (event) => {
        const toggle = event.target.closest("[data-bundle-toggle-picker]");
        if (toggle && this.contains(toggle)) {
          event.preventDefault();
          this.togglePicker(toggle.closest("[data-bundle-slot]"), toggle);
          return;
        }

        const pick = event.target.closest("[data-bundle-pick]");
        if (pick && this.contains(pick)) {
          event.preventDefault();
          this.choose(pick);
          return;
        }

        const clear = event.target.closest("[data-bundle-clear-slot]");
        if (clear && this.contains(clear)) {
          event.preventDefault();
          this.clearSlot(clear.closest("[data-bundle-slot]"));
          return;
        }

        const chip = event.target.closest("[data-bundle-option]");
        if (chip && this.contains(chip)) {
          event.preventDefault();
          this.chooseOption(chip);
          return;
        }

        const submit = event.target.closest("[data-bundle-submit]");
        if (submit && this.contains(submit)) {
          event.preventDefault();
          this.addToCart();
        }
      });
    }

    togglePicker(slotEl, toggle) {
      const opening = !slotEl.classList.contains("is-picking");

      // Only one picker open at a time.
      this.slotEls.forEach((el) => {
        el.classList.remove("is-picking");
        const t = el.querySelector("[data-bundle-toggle-picker]");
        if (t) t.setAttribute("aria-expanded", "false");
      });

      if (opening) {
        slotEl.classList.add("is-picking");
        toggle.setAttribute("aria-expanded", "true");
      }
    }

    choose(pick) {
      const slotEl = pick.closest("[data-bundle-slot]");
      const index = parseInt(slotEl.dataset.bundleSlot, 10);
      const slot = this.slots[index];
      const product = this.products.find(
        (p) => String(p.id) === pick.dataset.bundlePick
      );
      if (!product) return;

      slot.product = product;
      slot.options = this.defaultOptionsFor(product);
      this.resolveVariant(index);
      this.renderSlotOptions(index);

      slotEl.classList.remove("is-picking");
      const toggle = slotEl.querySelector("[data-bundle-toggle-picker]");
      if (toggle) toggle.setAttribute("aria-expanded", "false");

      this.update();
    }

    clearSlot(slotEl) {
      const index = parseInt(slotEl.dataset.bundleSlot, 10);
      const slot = this.slots[index];
      if (!slot || slot.locked) return;

      slot.product = null;
      slot.options = [];
      slot.variant = null;

      const host = slotEl.querySelector("[data-bundle-slot-options]");
      if (host) host.innerHTML = "";

      this.update();
    }

    chooseOption(chip) {
      const slotEl = chip.closest("[data-bundle-slot]");
      const index = parseInt(slotEl.dataset.bundleSlot, 10);
      const slot = this.slots[index];
      if (!slot.product) return;

      const optionIndex = parseInt(chip.dataset.bundleOption, 10);
      slot.options[optionIndex] = chip.dataset.bundleValue;

      this.resolveVariant(index);
      this.renderSlotOptions(index);
      this.update();
    }

    // Keep the locked slot in step with the main product form's variant.
    syncWithProductForm() {
      if (!this.lockFirst || !this.current) return;

      const form = this.closest("form");
      if (!form) return;

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;

      const apply = () => {
        const variantId = parseInt(idInput.value, 10);
        const variant = this.current.variants.find((v) => v.id === variantId);
        if (!variant) return;
        const slot = this.slots[0];
        if (!slot || !slot.locked) return;
        slot.options = variant.options.slice();
        slot.variant = variant;
        this.renderSlotOptions(0);
        this.update();
      };

      // The theme swaps the value programmatically, so watch the attribute
      // as well as ordinary input events.
      idInput.addEventListener("change", apply);
      new MutationObserver(apply).observe(idInput, {
        attributes: true,
        attributeFilter: ["value"],
      });
    }

    /* ---------------- cart ---------------- */

    async addToCart() {
      const p = this.prices();
      if (!p.complete) return;

      const btn = this.submitBtn;
      const restore = btn.textContent;
      btn.classList.add("is-loading");
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");

      const bundleId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const items = this.slots.map((slot) => ({
        id: slot.variant.id,
        quantity: 1,
        properties: {
          _bundle: this.bundleName,
          _bundle_id: bundleId,
        },
      }));

      try {
        const cartContainer = document.querySelector("cart-container");

        if (this.redirect || !cartContainer) {
          await fetch(`${window.Shopify.routes.cart_add_url}.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items }),
          });
          window.location.href = this.discountCode
            ? `${window.Shopify.routes.root_url}discount/${encodeURIComponent(
                this.discountCode
              )}?redirect=${encodeURIComponent(window.Shopify.routes.cart_url)}`
            : window.Shopify.routes.cart_url;
          return;
        }

        await cartContainer.add({ items });
      } catch (e) {
        console.error("[product-bundle] add to cart failed", e);
      } finally {
        btn.classList.remove("is-loading");
        btn.disabled = false;
        btn.setAttribute("aria-busy", "false");
        btn.textContent = restore;
      }
    }
  }

  if (!customElements.get("product-bundle")) {
    customElements.define("product-bundle", ProductBundle);
  }
})();
